import type { InstrumentEvent } from "features/market/api/InstrumentEvents";
import type { Operation, PositionWithAsset } from "types/Portfolio";

const DAY_MS = 24 * 60 * 60 * 1000;

export type InstrumentEventStatus = "upcoming" | "dueToday" | "overdue";

export interface PositionInstrumentEvent {
  event: InstrumentEvent;
  position: PositionWithAsset;
  daysUntil: number;
  status: InstrumentEventStatus;
}

export function normalizeInstrumentSymbol(symbol: string | undefined): string {
  return String(symbol ?? "")
    .trim()
    .toUpperCase()
    .replace(/([0-9])([CD])$/, "$1");
}

export function parseDateOnly(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function getDaysUntil(date: string, today = new Date()): number {
  return Math.ceil((parseDateOnly(date).getTime() - startOfLocalDay(today).getTime()) / DAY_MS);
}

export function getInstrumentEventsForPosition(
  position: PositionWithAsset,
  events: InstrumentEvent[]
): InstrumentEvent[] {
  const asset = position.assetId;
  if (!asset?.symbol || asset.type !== "market") return [];
  const symbol = normalizeInstrumentSymbol(asset.symbol);
  return events.filter((event) => (
    event.symbols?.some((eventSymbol) => normalizeInstrumentSymbol(eventSymbol) === symbol) ||
    normalizeInstrumentSymbol(event.ticker) === symbol ||
    normalizeInstrumentSymbol(event.normalizedTicker) === symbol
  ));
}

export function hasRecordedInstrumentEvent(
  event: InstrumentEvent,
  position: PositionWithAsset,
  operations: Operation[]
): boolean {
  const assetId = position.assetId?._id;
  if (!assetId) return false;
  const eventDate = parseDateOnly(event.date).getTime();

  return operations.some((operation) => {
    if (operation.side !== "amortization") return false;
    if (operation.assetId?._id !== assetId) return false;
    if (!operation.date) return true;
    return parseDateOnly(operation.date.slice(0, 10)).getTime() >= eventDate;
  });
}

export function getActionableInstrumentEvents({
  positions,
  operations,
  events,
  today = new Date(),
  upcomingDays = 30,
}: {
  positions: PositionWithAsset[];
  operations: Operation[];
  events: InstrumentEvent[];
  today?: Date;
  upcomingDays?: number;
}): PositionInstrumentEvent[] {
  const actionable: PositionInstrumentEvent[] = [];

  for (const position of positions) {
    for (const event of getInstrumentEventsForPosition(position, events)) {
      if (!["maturity", "amortization"].includes(event.eventType)) continue;
      if (event.amortization == null || event.amortization <= 0) continue;
      if (hasRecordedInstrumentEvent(event, position, operations)) continue;

      const daysUntil = getDaysUntil(event.date, today);
      if (daysUntil > upcomingDays) continue;

      actionable.push({
        event,
        position,
        daysUntil,
        status: daysUntil < 0 ? "overdue" : daysUntil === 0 ? "dueToday" : "upcoming",
      });
    }
  }

  return actionable.sort((a, b) => {
    const statusPriority = (item: PositionInstrumentEvent) => (
      item.status === "overdue" ? 0 : item.status === "dueToday" ? 1 : 2
    );
    const priority = statusPriority(a) - statusPriority(b);
    if (priority !== 0) return priority;
    return Math.abs(a.daysUntil) - Math.abs(b.daysUntil);
  });
}

export function getSuggestedAmortizationPrice(event: InstrumentEvent, position: PositionWithAsset): number {
  if (event.total != null && event.total > 0) return event.total;
  if (event.amortization != null && event.amortization > 0) return event.amortization;
  if (position.currentPrice != null && position.currentPrice > 0) return position.currentPrice;
  return Math.max(position.avgPrice, 0);
}

export function getSuggestedAmortizationQuantity(event: InstrumentEvent, position: PositionWithAsset): number {
  const quantity = Number(position.quantity) || 0;
  if (!event.saldo || !event.amortization || event.saldo <= 0) return quantity;
  const ratio = Math.min(Math.max(event.amortization / event.saldo, 0), 1);
  return Number((quantity * ratio).toFixed(6));
}
